import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api, formatKES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import PaymentDialog from "../components/PaymentDialog";
import StaffDocuments from "../components/StaffDocuments";
import { Briefcase, Wallet, Star, CheckCircle2, Rocket, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

export default function EmployeeDashboard() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [hires, setHires] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [chosenTier, setChosenTier] = useState(null);
  const [cities, setCities] = useState([]);
  const [features, setFeatures] = useState({ boost_fee_kes: 300, boost_duration_days: 7 });
  const [boostOpen, setBoostOpen] = useState(false);

  const load = async () => {
    const [p, h, pr, tr, c, f] = await Promise.all([
      api.get("/employees/me/profile"),
      api.get("/hires"),
      api.get("/payroll"),
      api.get("/tiers", { params: { audience: "employee" } }),
      api.get("/cities"),
      api.get("/config/features"),
    ]);
    setProfile(p.data);
    setHires(h.data);
    setPayroll(pr.data);
    setTiers(tr.data);
    setCities(c.data);
    setFeatures(f.data);
  };

  useEffect(() => { load(); }, []);

  const respondHire = async (id, status) => {
    await api.patch(`/hires/${id}`, { status });
    toast.success(status === "accepted" ? "Hire accepted" : "Hire declined");
    load();
  };

  const saveProfile = async () => {
    await api.patch("/employees/me/profile", {
      bio: profile.bio,
      skills: profile.skills,
      experience_years: Number(profile.experience_years),
      location: profile.location,
      photo_url: profile.photo_url,
      available: profile.available,
      current_city: profile.current_city,
      willing_cities: profile.willing_cities || [],
      available_anywhere: !!profile.available_anywhere,
      available_from: profile.available_from,
      immediately_available: !!profile.immediately_available,
    });
    toast.success("Profile saved");
    load();
  };

  const toggleWillingCity = (city) => {
    const current = profile.willing_cities || [];
    const next = current.includes(city) ? current.filter((c) => c !== city) : [...current, city];
    setProfile({ ...profile, willing_cities: next });
  };

  const boostRemaining = profile?.boosted_until
    ? Math.max(0, Math.round((new Date(profile.boosted_until) - new Date()) / (1000 * 60 * 60)))
    : 0;

  if (!profile) return <div className="p-20 text-center text-[#5B6478]">Loading...</div>;

  const totalEarned = payroll.filter((p) => p.status === "completed").reduce((s, p) => s + p.amount_net, 0);

  return (
    <div className="min-h-screen bg-[#F8F9FE]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="label-eyebrow mb-2">Staff dashboard</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">{profile.full_name}</h1>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <Badge className="bg-[#1E3A8A] hover:bg-[#1E3A8A] text-[#F8F9FE] capitalize">{profile.tier} Tier</Badge>
          {profile.verified && <Badge className="bg-[#7C3AED] hover:bg-[#7C3AED]">Verified</Badge>}
          <Badge variant={profile.status === "placed" ? "default" : "outline"} className="capitalize">{profile.status}</Badge>
        </div>

        <div className="grid lg:grid-cols-4 gap-4 mt-10">
          <Stat label="Job requests" value={hires.length} icon={Briefcase} />
          <Stat label="Active" value={hires.filter((h) => h.status === "accepted" || h.status === "active").length} icon={CheckCircle2} />
          <Stat label="Rating" value={profile.rating_avg?.toFixed(1) || "—"} icon={Star} />
          <Stat label="Earned (net)" value={formatKES(totalEarned)} icon={Wallet} />
        </div>

        <Tabs defaultValue="requests" className="mt-10">
          <TabsList className="bg-[#EEF0FA] h-11">
            <TabsTrigger value="requests" data-testid="tab-requests">Job Requests</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll History</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">My Profile</TabsTrigger>
            <TabsTrigger value="subscription" data-testid="tab-subscription">Subscription</TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="mt-6">
            <div className="space-y-3">
              {hires.length === 0 && <div className="text-center py-10 text-[#5B6478]">No requests yet</div>}
              {hires.map((h) => (
                <div key={h.id} className="bg-white border border-[#E4E7F5] rounded-md p-5 flex flex-wrap justify-between items-start gap-3" data-testid={`req-${h.id}`}>
                  <div>
                    <div className="font-display font-semibold">{h.customer_name}</div>
                    <div className="text-sm text-[#5B6478] mt-1">Start: {h.start_date} · {h.duration}</div>
                    <div className="text-sm mt-2">{h.description}</div>
                    <div className="text-sm font-semibold text-[#1E3A8A] mt-2">Offer: {formatKES(h.offered_rate)}</div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Badge className="capitalize" variant="outline">{h.status}</Badge>
                    {h.status === "pending" && (
                      <>
                        <Button size="sm" data-testid={`accept-${h.id}`} onClick={() => respondHire(h.id, "accepted")} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Accept</Button>
                        <Button size="sm" variant="outline" data-testid={`reject-${h.id}`} onClick={() => respondHire(h.id, "rejected")} className="border-[#B91C1C] text-[#B91C1C]">Reject</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-6">
            <StaffDocuments />
          </TabsContent>

          <TabsContent value="payroll" className="mt-6">
            <div className="bg-white border border-[#E4E7F5] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#EEF0FA] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">From</th>
                    <th className="px-4 py-3 font-semibold">Period</th>
                    <th className="px-4 py-3 font-semibold">Gross</th>
                    <th className="px-4 py-3 font-semibold">Net</th>
                    <th className="px-4 py-3 font-semibold">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {payroll.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#5B6478]">No payroll records</td></tr>}
                  {payroll.map((p) => (
                    <tr key={p.id} className="border-t border-[#E4E7F5]">
                      <td className="px-4 py-3">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">{p.paid_by_name}</td>
                      <td className="px-4 py-3">{p.period}</td>
                      <td className="px-4 py-3">{formatKES(p.amount_gross)}</td>
                      <td className="px-4 py-3 font-semibold text-[#1E3A8A]">{formatKES(p.amount_net)}</td>
                      <td className="px-4 py-3 font-mono text-xs">{p.receipt || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <div className="bg-white border border-[#E4E7F5] rounded-md p-6 max-w-3xl">
              {/* Featured Boost card */}
              <div
                data-testid="boost-card"
                className={`rounded-lg p-4 mb-6 border ${profile.is_boosted ? "bg-gradient-to-br from-[#7C3AED]/10 to-[#1E3A8A]/10 border-[#7C3AED]" : "bg-[#EEF0FA] border-[#E4E7F5]"}`}
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${profile.is_boosted ? "bg-[#7C3AED] text-white" : "bg-white border border-[#E4E7F5] text-[#1E3A8A]"}`}>
                      {profile.is_boosted ? <Sparkles className="w-5 h-5" /> : <Rocket className="w-5 h-5" />}
                    </div>
                    <div>
                      <div className="font-display font-bold text-[#0F1E4B]">
                        {profile.is_boosted ? "Your profile is featured" : "Boost your profile"}
                      </div>
                      <div className="text-xs text-[#5B6478] mt-0.5">
                        {profile.is_boosted
                          ? `Pinned to the top of Browse for ~${boostRemaining} more hours.`
                          : `Pay ${formatKES(features.boost_fee_kes)} to pin your profile to the top of Browse for ${features.boost_duration_days} days.`}
                      </div>
                    </div>
                  </div>
                  {!profile.is_boosted && (
                    <Button
                      data-testid="boost-btn"
                      onClick={() => setBoostOpen(true)}
                      className="bg-[#7C3AED] hover:bg-[#5B21B6] text-white gap-2"
                    >
                      <Rocket className="w-4 h-4" /> Boost for {formatKES(features.boost_fee_kes)}
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="label-eyebrow">Photo URL</Label>
                  <Input data-testid="prof-photo" value={profile.photo_url || ""} onChange={(e) => setProfile({ ...profile, photo_url: e.target.value })} className="mt-2 border-[#E4E7F5]" />
                </div>
                <div className="col-span-2">
                  <Label className="label-eyebrow">Bio</Label>
                  <Textarea data-testid="prof-bio" rows={3} value={profile.bio || ""} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} className="mt-2 border-[#E4E7F5]" />
                </div>
                <div className="col-span-2">
                  <Label className="label-eyebrow">Skills (comma separated)</Label>
                  <Input data-testid="prof-skills" value={profile.skills?.join(", ") || ""} onChange={(e) => setProfile({ ...profile, skills: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="mt-2 border-[#E4E7F5]" />
                </div>
                <div>
                  <Label className="label-eyebrow">Experience (years)</Label>
                  <Input data-testid="prof-exp" type="number" value={profile.experience_years || 0} onChange={(e) => setProfile({ ...profile, experience_years: e.target.value })} className="mt-2 border-[#E4E7F5]" />
                </div>
                <div>
                  <Label className="label-eyebrow">Neighborhood / Estate</Label>
                  <Input data-testid="prof-loc" placeholder="e.g. Westlands, Nairobi" value={profile.location || ""} onChange={(e) => setProfile({ ...profile, location: e.target.value })} className="mt-2 border-[#E4E7F5]" />
                </div>

                {/* City / cities */}
                <div>
                  <Label className="label-eyebrow">Current City</Label>
                  <Select value={profile.current_city || ""} onValueChange={(v) => setProfile({ ...profile, current_city: v })}>
                    <SelectTrigger data-testid="prof-current-city" className="mt-2 border-[#E4E7F5]">
                      <SelectValue placeholder="Select your city" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-eyebrow">Available to start</Label>
                  <Input
                    data-testid="prof-available-from"
                    type="date"
                    disabled={!!profile.immediately_available}
                    value={profile.available_from || ""}
                    onChange={(e) => setProfile({ ...profile, available_from: e.target.value })}
                    className="mt-2 border-[#E4E7F5]"
                  />
                  <label className="flex items-center gap-2 text-xs text-[#5B6478] mt-2">
                    <input
                      data-testid="prof-immediate"
                      type="checkbox"
                      checked={!!profile.immediately_available}
                      onChange={(e) => setProfile({ ...profile, immediately_available: e.target.checked })}
                    />
                    Available immediately
                  </label>
                </div>

                {/* Willing to work in */}
                <div className="col-span-2 rounded-md p-4 bg-[#F8F9FE] border border-[#E4E7F5]">
                  <div className="flex items-center justify-between">
                    <Label className="label-eyebrow">Willing to work in</Label>
                    <label className="flex items-center gap-2 text-xs text-[#5B6478]">
                      <Switch
                        data-testid="prof-anywhere-switch"
                        checked={!!profile.available_anywhere}
                        onCheckedChange={(v) => setProfile({ ...profile, available_anywhere: v })}
                      />
                      Anywhere in Kenya
                    </label>
                  </div>
                  {profile.available_anywhere ? (
                    <div className="mt-3 text-sm text-[#5B6478] italic">
                      You'll appear in results for every Kenyan city.
                    </div>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {cities.map((c) => {
                        const active = (profile.willing_cities || []).includes(c);
                        return (
                          <button
                            key={c}
                            type="button"
                            data-testid={`willing-city-${c.toLowerCase().replace(/\s+/g, '-')}`}
                            onClick={() => toggleWillingCity(c)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-[#1E3A8A] border-[#1E3A8A] text-white" : "bg-white border-[#E4E7F5] text-[#5B6478] hover:border-[#1E3A8A]"}`}
                          >
                            {active && <X className="w-3 h-3 inline-block mr-1 -mt-0.5" />}
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="col-span-2 bg-[#EEF0FA] border border-[#E4E7F5] rounded-md p-4 text-sm text-[#5B6478]">
                  <strong className="text-[#0F1E4B]">Rate:</strong> Your pay rate is set by the bureau based on your qualifications and experience. Rate reviews happen every 6 months. Contact KSB via the Reports channel for a review request.
                </div>
                <div className="col-span-2 flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <input data-testid="prof-available" type="checkbox" checked={profile.available} onChange={(e) => setProfile({ ...profile, available: e.target.checked })} /> Available for hire
                  </label>
                </div>
              </div>
              <Button data-testid="prof-save" onClick={saveProfile} className="mt-6 bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Save profile</Button>
            </div>
          </TabsContent>

          <TabsContent value="subscription" className="mt-6">
            <div className="grid md:grid-cols-3 gap-4">
              {tiers.map((t) => {
                const isCurrent = user?.current_tier_id === t.id;
                return (
                  <div key={t.id} data-testid={`emp-tier-tile-${t.id}`} className={`price-tile ${isCurrent ? "is-current" : ""}`}>
                    {isCurrent && <span className="price-tile-ribbon">Current</span>}
                    <div className="font-display font-bold text-xl">{t.name}</div>
                    <div className="mt-2 font-display text-3xl font-extrabold text-[#1E3A8A]">
                      {formatKES(t.price)}<span className="text-xs text-[#5B6478] font-normal">/{t.period === "monthly" ? "mo" : "once"}</span>
                    </div>
                    <ul className="mt-5 space-y-2 text-sm">
                      {t.features.map((f, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#1E3A8A] mt-0.5 shrink-0" /> {f}</li>)}
                    </ul>
                    <Button
                      data-testid={`emp-subscribe-${t.id}`}
                      disabled={isCurrent || t.price === 0}
                      onClick={() => { setChosenTier(t); setPayOpen(true); }}
                      className="w-full mt-6 bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]"
                    >
                      {isCurrent ? "Current plan" : t.price === 0 ? "Free plan" : "Upgrade"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {chosenTier && (
        <PaymentDialog
          open={payOpen}
          onOpenChange={setPayOpen}
          amount={chosenTier.price}
          purpose="subscription"
          tierId={chosenTier.id}
          defaultPhone={user?.phone}
          title={`Upgrade to ${chosenTier.name}`}
          onSuccess={async () => { await refresh(); load(); }}
        />
      )}

      <PaymentDialog
        open={boostOpen}
        onOpenChange={setBoostOpen}
        amount={features.boost_fee_kes}
        purpose="boost"
        defaultPhone={user?.phone}
        title="Feature my profile"
        description={`Pin your profile to the top of Browse for ${features.boost_duration_days} days.`}
        onSuccess={() => load()}
      />
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[#E4E7F5] rounded-md p-5 flex items-center justify-between">
      <div>
        <div className="label-eyebrow">{label}</div>
        <div className="font-display text-2xl font-bold mt-1">{value}</div>
      </div>
      <Icon className="w-6 h-6 text-[#1E3A8A]" />
    </div>
  );
}
